import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl p-4 sm:p-6 lg:p-8 border border-blue-100" }
const _hoisted_2 = { class: "bg-white rounded-lg sm:rounded-xl shadow-md sm:shadow-xl p-3 sm:p-4 lg:p-6 hover:shadow-2xl transition-shadow duration-300" }

import { computed } from 'vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import { 
  GridComponent, 
  TooltipComponent, 
  LegendComponent
} from 'echarts/components';
import { VentasPeriodoInterface } from '../interfaces/ventasPeriodo.interface';

interface Props {
  data?: VentasPeriodoInterface;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VentasPeriodoChart',
  props: {
    data: {},
    loading: { type: Boolean }
  },
  setup(__props: any) {

use([
  CanvasRenderer, 
  BarChart,
  GridComponent, 
  TooltipComponent, 
  LegendComponent
]);

const props = __props;

// Altura responsiva del gráfico
const chartHeight = computed(() => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 640) return '300px'; // móvil
    if (window.innerWidth < 1024) return '400px'; // tablet
    return '500px'; // desktop
  }
  return '500px';
});

const chartOption = computed(() => {
  const ventas = props.data?.data.ventas || [];
  
  // Agrupar ventas por fecha
  const ventasPorFecha = ventas.reduce((acc, venta) => {
    const fecha = new Date(venta.createdAt).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: 'short'
    });
    if (!acc[fecha]) {
      acc[fecha] = { total: 0, cantidad: 0 };
    }
    acc[fecha].total += parseFloat(venta.totalPedido);
    acc[fecha].cantidad += 1;
    return acc;
  }, {} as Record<string, { total: number; cantidad: number }>);

  const fechas = Object.keys(ventasPorFecha);
  const totales = Object.values(ventasPorFecha).map(v => v.total);
  const cantidades = Object.values(ventasPorFecha).map(v => v.cantidad);

  // Configuración responsiva
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isTablet = typeof window !== 'undefined' && window.innerWidth < 1024;

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: '#3b82f6',
      borderWidth: 2,
      textStyle: {
        color: '#fff',
        fontSize: isMobile ? 11 : 13
      },
      padding: isMobile ? 12 : 16,
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(59, 130, 246, 0.15)'
        }
      },
      formatter: (params: any) => {
        const fecha = params[0].axisValue;
        const total = params[0].value;
        const cantidad = params[1]?.value || 0;
        const promedio = cantidad > 0 ? (total / cantidad).toFixed(2) : '0.00';
        
        return `
          <div style="min-width: ${isMobile ? '160px' : '200px'};">
            <div style="font-weight: 700; margin-bottom: ${isMobile ? '8px' : '12px'}; color: #60a5fa; font-size: ${isMobile ? '13px' : '15px'}; border-bottom: 2px solid rgba(59, 130, 246, 0.3); padding-bottom: ${isMobile ? '6px' : '8px'};">
              📅 ${fecha}
            </div>
            <div style="margin-bottom: ${isMobile ? '6px' : '8px'}; display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center;">
                <span style="display: inline-block; width: ${isMobile ? '10px' : '14px'}; height: ${isMobile ? '10px' : '14px'}; border-radius: 4px; background: linear-gradient(135deg, #3b82f6, #2563eb); margin-right: ${isMobile ? '6px' : '10px'}; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);"></span>
                <span style="color: #cbd5e1; font-size: ${isMobile ? '11px' : '13px'};">Ingresos</span>
              </div>
              <span style="font-weight: 700; color: #fff; font-size: ${isMobile ? '13px' : '15px'};">S/ ${Number(total).toFixed(2)}</span>
            </div>
            <div style="margin-bottom: ${isMobile ? '6px' : '8px'}; display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center;">
                <span style="display: inline-block; width: ${isMobile ? '10px' : '14px'}; height: ${isMobile ? '10px' : '14px'}; border-radius: 4px; background: linear-gradient(135deg, #10b981, #059669); margin-right: ${isMobile ? '6px' : '10px'}; box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);"></span>
                <span style="color: #cbd5e1; font-size: ${isMobile ? '11px' : '13px'};">Pedidos</span>
              </div>
              <span style="font-weight: 700; color: #fff; font-size: ${isMobile ? '13px' : '15px'};">${cantidad}</span>
            </div>
            <div style="padding-top: ${isMobile ? '8px' : '10px'}; border-top: 1px solid rgba(59, 130, 246, 0.3); margin-top: 4px; display: flex; align-items: center; justify-content: space-between;">
              <span style="color: #94a3b8; font-size: ${isMobile ? '10px' : '12px'};">💰 Promedio/Pedido</span>
              <span style="font-weight: 700; color: #fbbf24; font-size: ${isMobile ? '12px' : '14px'};">S/ ${promedio}</span>
            </div>
          </div>
        `;
      }
    },
    legend: {
      data: ['💵 Ingresos Totales', '📦 Cantidad de Pedidos'],
      top: isMobile ? 10 : 15,
      textStyle: {
        color: '#1f2937',
        fontSize: isMobile ? 11 : isTablet ? 12 : 14,
        fontWeight: 600
      },
      itemWidth: isMobile ? 18 : 24,
      itemHeight: isMobile ? 12 : 16,
      itemGap: isMobile ? 10 : 20,
      orient: isMobile ? 'horizontal' : 'horizontal'
    },
    grid: {
      left: isMobile ? '5%' : '3%',
      right: isMobile ? '5%' : '3%',
      bottom: isMobile ? '8%' : '5%',
      top: isMobile ? '25%' : '20%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: fechas,
      axisLine: {
        lineStyle: {
          color: '#e5e7eb',
          width: isMobile ? 1.5 : 2
        }
      },
      axisLabel: {
        color: '#6b7280',
        fontSize: isMobile ? 9 : isTablet ? 10 : 12,
        fontWeight: 500,
        rotate: fechas.length > 10 || isMobile ? 35 : 0,
        margin: isMobile ? 10 : 15,
        interval: isMobile && fechas.length > 7 ? 1 : 0
      },
      axisTick: {
        show: false
      }
    },
    yAxis: [
      {
        type: 'value',
        name: isMobile ? '💵 S/' : '💵 Ingresos (S/)',
        position: 'left',
        nameTextStyle: {
          color: '#3b82f6',
          fontSize: isMobile ? 10 : isTablet ? 12 : 14,
          fontWeight: 700,
          padding: [0, 0, isMobile ? 5 : 10, 0]
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#3b82f6',
            width: isMobile ? 2 : 3
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: isMobile ? 9 : isTablet ? 10 : 12,
          fontWeight: 600,
          formatter: (value: number) => {
            if (value >= 1000) return isMobile ? `${(value / 1000).toFixed(1)}k` : `S/ ${(value / 1000).toFixed(1)}k`;
            return isMobile ? `${value.toFixed(0)}` : `S/ ${value.toFixed(0)}`;
          }
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
            width: isMobile ? 1 : 1.5
          }
        }
      },
      {
        type: 'value',
        name: isMobile ? '📊' : '📊 Cantidad',
        position: 'right',
        nameTextStyle: {
          color: '#10b981',
          fontSize: isMobile ? 10 : isTablet ? 12 : 14,
          fontWeight: 700,
          padding: [0, 0, isMobile ? 5 : 10, 0]
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#10b981',
            width: isMobile ? 2 : 3
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: isMobile ? 9 : isTablet ? 10 : 12,
          fontWeight: 600
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: [
      {
        name: '💵 Ingresos Totales',
        type: 'bar',
        data: totales,
        barWidth: isMobile ? '25%' : '35%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#60a5fa' },
              { offset: 0.4, color: '#3b82f6' },
              { offset: 1, color: '#1d4ed8' }
            ]
          },
          borderRadius: [isMobile ? 8 : 12, isMobile ? 8 : 12, 0, 0],
          shadowBlur: isMobile ? 10 : 20,
          shadowColor: 'rgba(59, 130, 246, 0.5)',
          shadowOffsetY: isMobile ? 4 : 8,
          shadowOffsetX: 0
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#93c5fd' },
                { offset: 0.4, color: '#60a5fa' },
                { offset: 1, color: '#3b82f6' }
              ]
            },
            shadowBlur: isMobile ? 15 : 30,
            shadowColor: 'rgba(59, 130, 246, 0.7)',
            shadowOffsetY: isMobile ? 6 : 12
          },
          label: {
            show: !isMobile,
            position: 'top',
            color: '#1f2937',
            fontWeight: 700,
            fontSize: 13,
            formatter: (params: any) => `S/ ${params.value.toFixed(2)}`
          }
        },
        barGap: '30%'
      },
      {
        name: '📦 Cantidad de Pedidos',
        type: 'bar',
        yAxisIndex: 1,
        data: cantidades,
        barWidth: isMobile ? '25%' : '35%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#6ee7b7' },
              { offset: 0.4, color: '#10b981' },
              { offset: 1, color: '#047857' }
            ]
          },
          borderRadius: [isMobile ? 8 : 12, isMobile ? 8 : 12, 0, 0],
          shadowBlur: isMobile ? 10 : 20,
          shadowColor: 'rgba(16, 185, 129, 0.5)',
          shadowOffsetY: isMobile ? 4 : 8,
          shadowOffsetX: 0
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#a7f3d0' },
                { offset: 0.4, color: '#34d399' },
                { offset: 1, color: '#10b981' }
              ]
            },
            shadowBlur: isMobile ? 15 : 30,
            shadowColor: 'rgba(16, 185, 129, 0.7)',
            shadowOffsetY: isMobile ? 6 : 12
          },
          label: {
            show: !isMobile,
            position: 'top',
            color: '#1f2937',
            fontWeight: 700,
            fontSize: 13,
            formatter: (params: any) => `${params.value} pedidos`
          }
        }
      }
    ],
    animationDuration: 1000,
  };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[0] || (_cache[0] = _createElementVNode("div", {
      class: "mb-4 sm:mb-6 lg:mb-8 pb-4 sm:pb-6 border-b-2 border-gradient-to-r from-blue-400 to-indigo-500",
      style: {"border-image":"linear-gradient(90deg, #3b82f6, #6366f1) 1"}
    }, [
      _createElementVNode("h3", { class: "text-xl sm:text-2xl lg:text-3xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1 sm:mb-2" }, " Ventas por Período "),
      _createElementVNode("p", { class: "text-sm sm:text-base text-gray-600 font-medium" }, "Análisis visual de ingresos y pedidos")
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(VChart), {
        option: chartOption.value,
        loading: __props.loading,
        class: "w-full",
        style: _normalizeStyle({ height: chartHeight.value }),
        autoresize: ""
      }, null, 8, ["option", "loading", "style"])
    ]),
    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "mt-4 sm:mt-6 flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-500 px-2" }, [
      _createElementVNode("svg", {
        class: "w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0",
        fill: "currentColor",
        viewBox: "0 0 20 20"
      }, [
        _createElementVNode("path", {
          "fill-rule": "evenodd",
          d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z",
          "clip-rule": "evenodd"
        })
      ]),
      _createElementVNode("span", { class: "font-medium text-center" }, "Pasa el cursor sobre las barras para ver más detalles")
    ], -1))
  ]))
}
}

})